/**
 * WP-577 / TEN-2918 follow-up: the honest response shape for `capture action=batch`
 * run with `preview: true`.
 *
 * Why this exists. `handleCapture`'s single path has always had a dedicated preview
 * branch that returns "Preview: would capture … — no DB writes" with
 * `outcome: "preview"`. `handleBatchCapture` had no equivalent: its response shaping
 * is unconditional, so a preview batch returned the REAL-create surface —
 * "# Batch Capture Complete", "**N** created", per-entry `status: "draft"`, and a
 * `next` offering `entries action=commit` on an entryId that was never written.
 *
 * That was invisible until now because batch preview threw `ArgumentValidationError`
 * at the gateway before reaching this code (the parity gap this PR closes). Making
 * `preview` forward correctly is what turned the dishonest report into a LIVE agent
 * surface — the same "the fix exposed a latent bug" shape as the auto-commit block
 * guarded in `handleBatchCapture`, found by driving the real MCP connector process
 * against a seeded preview.
 *
 * Extracted rather than inlined: `smart-capture.ts` is grandfathered shrink-only
 * under STD-2 / DEC-1504, so the additions are paid for here (same reasoning as
 * `lib/textMatch.ts` in this PR) instead of widening that file's baseline.
 */
// zod/v3 — must match smart-capture.ts's specifier exactly; a bare "zod" import resolves
// to v4 here and the two instances' types are not assignable across a z.union().
import { z } from "zod/v3";
import { success, type NextAction } from "../envelope.js";
// WP-575 review round (P1, TEN-1126): the outcome-unknown cohort (type, split, structured
// mapping, zod shape, rendering) is ONE responsibility shared with smart-capture.ts's
// real-create batch path — see that module's doc comment for the full reasoning.
import {
  splitBatchNotOk,
  unknownOutcomeEntrySchema,
  unknownOutcomeSummaryNote,
  renderOutcomeUnknownSection,
  type BatchTimeoutOutcome,
  type UnknownOutcomeEntry,
} from "./batchTimeoutCohort.js";

// Re-exported so smart-capture.ts's real-create batch path can pull the whole outcome-unknown
// cohort API (used identically on both batch surfaces) from ONE import specifier.
export { splitBatchNotOk, describeBatchEntryOutcome, unknownOutcomeSummaryNote, renderOutcomeUnknownSection, type BatchTimeoutOutcome, type UnknownOutcomeEntry } from "./batchTimeoutCohort.js";

/**
 * The real-create branch of the `capture action=batch` output contract. Moved here from
 * smart-capture.ts (STD-2 / DEC-1504 shrink-only) so both branches of the batch contract
 * and the builder that produces the preview one live in a single cohesive module —
 * re-exported from smart-capture.ts for existing importers.
 */
export const batchCaptureRealOutputSchema = z.object({
  captured: z.array(z.object({
    entryId: z.string(),
    collection: z.string(),
    name: z.string(),
    // "draft_on_failure" is the status emitted when an auto-commit is REFUSED/failed (the entry
    // stays a draft) — the same path that now also emits coherencyRefusal. The strict schema must
    // accept it or a refused response fails to validate on `status` (codex review, completing the
    // coherencyRefusal contract fix).
    status: z.enum(["draft", "committed", "proposed", "draft_on_failure"]),
    classifiedBy: z.enum(["llm", "heuristic", "explicit"]).optional(),
    confidence: z.number().optional(),
    confidenceTier: z.enum(["high", "medium", "low"]).optional(),
    warnings: z.array(z.string()).optional(),
    normalization: z.object({
      remapped: z.record(z.string()).optional(),
      rejected: z.array(z.string()).optional(),
    }).optional(),
    // TEN-2365: capture-time authority-domain proposal slug (PENDING ratification), when filed.
    domain: z.string().optional(),
    // WP-465 surface parity: structured coherency refusal for a gate-refused auto-commit (batch
    // shape). Loose record — same single-source rationale as captureSuccessOutputSchema.
    coherencyRefusal: z.record(z.unknown()).optional(),
    // WP-485 Slice 2b round 4 (Codex P2, FEAT-1370): per-entry contradiction advisory, emitted
    // into structuredContent but previously undeclared here. Not `.strict()` so this one was
    // silently stripped rather than rejected — declared anyway so batch consumers actually see it.
    contradictionAdvisory: z.record(z.unknown()).optional(),
  })),
  total: z.number(),
  failed: z.number(),
  committed: z.number(),
  proposed: z.number(),
  drafts: z.number(),
  classified: z.number().optional(),
  autoCommitApplied: z.boolean(),
  skippedLowConfidence: z.array(z.object({
    index: z.number(),
    name: z.string(),
    suggestedCollection: z.string().optional(),
    confidence: z.number().optional(),
    alternatives: z.array(z.object({
      collection: z.string(),
      confidence: z.number(),
    })).optional(),
  })).optional(),
  failedEntries: z.array(z.object({
    index: z.number(),
    collection: z.string(),
    name: z.string(),
    error: z.string(),
  })).optional(),
  // WP-575 review round (P1, TEN-1126): a DISTINCT cohort from `failedEntries` — additive,
  // never removes or retypes an existing field (repo's schema rule). See
  // lib/batchTimeoutCohort.ts's doc comment for why this must stay separate.
  unknownOutcomeEntries: z.array(unknownOutcomeEntrySchema).optional(),
});

/**
 * The batch-preview branch of the `capture` output contract — a DIFFERENT shape from a
 * real batch: no `captured`/`drafts`/`committed` (nothing was written, so reporting
 * draft counts would lie), and `wouldCapture` entries carry no `status` for the same
 * reason. Lives beside its builder so the two cannot drift.
 */
export const batchCapturePreviewOutputSchema = z.object({
  outcome: z.literal("preview"),
  wouldCapture: z.array(z.object({
    // Fix 1 (WP-577 review re-review, TEN-2918 follow-up): optional, not required — see
    // WouldCaptureEntry's doc comment. Absent exactly when `entryIdAssignedAtCapture` is true.
    entryId: z.string().optional(),
    entryIdAssignedAtCapture: z.literal(true).optional(),
    collection: z.string(),
    name: z.string(),
    classifiedBy: z.string().optional(),
    confidence: z.number().optional(),
    warnings: z.array(z.string()).optional(),
  })),
  // WP-577 review follow-up (P2, TEN-2918): entries the server's WHY/glossary gate says would
  // be BLOCKED on a real (non-preview) create — a distinct cohort from `wouldCapture` so the
  // structured response never claims a governance-refused capture would succeed. Optional/
  // additive: absent (or empty) on every response this route produced before this fix.
  wouldBlock: z.array(z.object({
    entryId: z.string().optional(),
    entryIdAssignedAtCapture: z.literal(true).optional(),
    collection: z.string(),
    name: z.string(),
    blockingReason: z.string().optional(),
    warnings: z.array(z.string()).optional(),
  })).optional(),
  requested: z.number(),
  total: z.number(),
  failed: z.number(),
  skippedLowConfidence: z.array(z.object({
    index: z.number(),
    name: z.string(),
    suggestedCollection: z.string().optional(),
    confidence: z.number().optional(),
  })).optional(),
  failedEntries: z.array(z.object({
    index: z.number(),
    collection: z.string(),
    name: z.string(),
    error: z.string(),
  })).optional(),
  // WP-575 review round (P1, TEN-1126): sibling of the same field on
  // batchCaptureRealOutputSchema — preview batches reach the SAME unconditional
  // `chain.createEntry` catch a real batch does, so the discriminator exists here too.
  unknownOutcomeEntries: z.array(unknownOutcomeEntrySchema).optional(),
});

/** The full batch output contract: a real create, or a preview. Mirrors captureOutputSchema's union shape on the single path. */
export const batchCaptureOutputSchema = z.union([
  batchCaptureRealOutputSchema,
  batchCapturePreviewOutputSchema,
]);

/**
 * One entry the batch WOULD have created. Deliberately never carries `status` — there is no
 * persisted entry to have one.
 *
 * Fix 1 (WP-577 review re-review, TEN-2918 follow-up): `entryId` is present ONLY when the
 * caller supplied it explicitly for that item. A batch preview cannot predict an auto-allocated
 * ID: each `createEntry` call is independent and stateless (`peekNextEntryId` reads the same
 * counter row without advancing it — TEN-2918's original fix), and IDs are allocated
 * per-collection-prefix server-side, so two auto-ID entries sharing a prefix within ONE preview
 * batch would both `peekNextEntryId` the SAME next value. A real (non-preview) batch instead
 * allocates them consecutively, advancing the counter after each write — the two paths cannot
 * agree, so reporting the predicted value here would silently duplicate it across every
 * auto-ID item and mislead a caller planning links off the receipt. `entryIdAssignedAtCapture`
 * is set instead whenever `entryId` is omitted for this reason.
 */
export type WouldCaptureEntry = {
  entryId?: string;
  entryIdAssignedAtCapture?: boolean;
  collection: string;
  name: string;
  classifiedBy?: string;
  confidence?: number;
  warnings?: string[];
};

/**
 * One entry the batch WOULD have had BLOCKED on a real (non-preview) create — e.g. a governed
 * capture missing its required WHY, or a glossary insert-block (createEntryPreview.ts's
 * `wouldBlock`/`blockingReason`). Kept distinct from `WouldCaptureEntry` so the preview surface
 * never reports a would-be-refused capture as would-succeed (WP-577 review follow-up, P2,
 * TEN-2918).
 */
export type WouldBlockEntry = {
  // Fix 1 (TEN-2918 follow-up): same caller-supplied-only rule as WouldCaptureEntry.entryId —
  // a would-block verdict is still keyed off a predicted ID, subject to the same collision.
  entryId?: string;
  entryIdAssignedAtCapture?: boolean;
  collection: string;
  name: string;
  blockingReason?: string;
  warnings?: string[];
};

export type BatchPreviewInput = {
  /** Entries the batch would have written, in result order. */
  wouldCapture: WouldCaptureEntry[];
  /** Entries the server says would be BLOCKED on a real create (governance/glossary gates). */
  wouldBlock?: WouldBlockEntry[];
  /** Entries that failed before any write would have happened. */
  failed: Array<{ index: number; collection: string; name: string; error: string }>;
  /**
   * WP-575 review round (P1, TEN-1126): entries whose `chain.createEntry` gateway-timed-out —
   * outcome unknown, NOT proven failed. Kept out of `failed` on purpose (see
   * `UnknownOutcomeEntry`'s doc comment).
   */
  unknownOutcome?: UnknownOutcomeEntry[];
  /** Entries skipped because classification confidence was too low. */
  skipped: Array<{ index: number; name: string; suggestedCollection?: string; confidence?: number }>;
  /** Size of the caller's original `items[]`. */
  requested: number;
  /**
   * The caller's original batch payload (one entry per `items[]` element), threaded through so
   * the "Capture for real" next-action can supply a schema-valid, non-empty `items` array
   * instead of the unusable `{ action: "batch" }` alone (WP-577 review follow-up, P2, TEN-2918).
   * Omitted (or empty) callers get no "Capture for real" next-action rather than a broken one.
   */
  originalItems?: unknown[];
  /**
   * Fix 1 (WP-577 review round 3, TEN-2918 follow-up): the batch's own top-level `autoCommit`,
   * threaded into the "Capture for real" next-action so a caller who previewed with
   * `autoCommit: false` doesn't silently get the DEFAULT auto-commit behavior on retry — the
   * exact `buildCaptureForRealNextAction`/`captureForRealAction.ts` trap on the single path,
   * mirrored here. `undefined` (never supplied) is omitted so the server's own default still
   * governs; `false` is an explicit caller choice and MUST survive — never dropped as falsy.
   */
  originalAutoCommit?: boolean;
};

/**
 * Builds the summary line. Mirrors the single path's "— no DB writes" suffix so both
 * capture surfaces read the same way, and never uses the word "captured" (past tense
 * on a dry run is precisely the lie this module exists to prevent).
 */
export function buildBatchPreviewSummary({ wouldCapture, wouldBlock = [], failed, unknownOutcome = [], skipped, requested }: BatchPreviewInput): string {
  const noun = requested === 1 ? "entry" : "entries";
  const blockedNote = wouldBlock.length > 0 ? `, ${wouldBlock.length} would be BLOCKED` : "";
  const failedNote = failed.length > 0 ? `, ${failed.length} would fail` : "";
  const unknownNote = unknownOutcomeSummaryNote(unknownOutcome);
  const skippedNote = skipped.length > 0 ? `, ${skipped.length} skipped (low confidence)` : "";
  return `Preview: would capture ${wouldCapture.length} of ${requested} ${noun}${blockedNote}${failedNote}${unknownNote}${skippedNote} — no DB writes.`;
}

/**
 * The full batch-preview tool result — markdown body + structured envelope.
 *
 * `next` offers ONLY "capture for real", and only when `originalItems` was supplied —
 * `capture action=batch` requires a non-empty `items[]` (batchCaptureSchema `.min(1)`), so a
 * next-action with no items would fail argument validation the instant an agent followed it
 * (WP-577 review follow-up, P2, TEN-2918). It otherwise deliberately omits both actions the
 * real-create path offers: `entries action=commit` (would target an entry that does not exist)
 * and `relations action=suggest` (same). An agent following a `next` hint into a 404 — or a
 * validation error — is worse than no hint.
 */
/** One `created` entry as seen by the preview-cohort split — the fields `buildBatchPreviewFromResults` reads off `handleBatchCapture`'s per-entry result, never the full internal result shape. */
export type BatchCreatedForPreview = {
  entryId: string;
  collection: string;
  name: string;
  classifiedBy?: string;
  confidence?: number;
  warnings?: string[];
  // WP-577 review follow-up (P2, TEN-2918): this entry's own would-block verdict
  // (createEntryPreview.ts's `wouldBlock`/`blockingReason`), undefined on the real-create path.
  wouldBlock?: boolean;
  blockingReason?: string;
  // Fix 1 (TEN-2918 follow-up): true when `entryId` was auto-allocated (no caller-supplied
  // `entryId` for this item) — see WouldCaptureEntry's doc comment.
  entryIdAssignedAtCapture?: boolean;
};

export type BatchFailedForPreview = {
  entryIdx: number;
  collection: string;
  name: string;
  error?: string;
  // WP-575 review round (P1, TEN-1126): set only when this failure came from a
  // `chain.createEntry` gateway timeout — see `BatchTimeoutOutcome`'s doc comment. Presence
  // (not the boolean fields inside it) is the discriminator `buildBatchPreviewFromResults`
  // filters on to route this entry to `unknownOutcome` instead of `failed`.
  timeoutOutcome?: BatchTimeoutOutcome;
};

export type BatchSkippedForPreview = { index: number; name: string; suggestedCollection?: string; confidence?: number };

/**
 * The shared shape of a batch entry's early/mid-loop failure result — `handleBatchCapture`'s
 * "collection not found", "entryId not allowed" (BR-111), chunk-flush-catch, and outer-catch
 * sites all built this exact literal by hand (`entryId: ""`, `ok: false`, `autoLinks: 0`,
 * `advisedLinks: 0`, `status: "draft"`) with only `entryIdx`/`name`/`collection`/classification/
 * `error` varying. Extracted here (STD-2 / DEC-1504 shrink-only — `smart-capture.ts` is
 * grandfathered over budget, so new call sites pay their cost here, never by widening it) so the
 * four call sites read as one line each instead of repeating the same 10-field literal.
 */
export function buildBatchEntryFailure(args: {
  entryIdx: number;
  name: string;
  collection: string;
  classifiedBy: "llm" | "heuristic" | "explicit" | undefined;
  confidence: number | undefined;
  confidenceTier: "high" | "medium" | "low" | undefined;
  error: string;
  // WP-575 review round (P1, TEN-1126/TEN-2917): set by the ONE call site
  // (smart-capture.ts's outer per-entry catch) that can observe a `chain.createEntry`
  // gateway timeout — see `BatchTimeoutOutcome`'s doc comment. Every other caller of this
  // builder ("collection not found", BR-111 entryId rejection, chunk-flush-catch) fails
  // before or independent of that mutation, so they never pass this.
  timeoutOutcome?: BatchTimeoutOutcome;
}) {
  return {
    entryIdx: args.entryIdx,
    name: args.name,
    collection: args.collection,
    entryId: "",
    ok: false as const,
    autoLinks: 0,
    advisedLinks: 0,
    status: "draft" as const,
    classifiedBy: args.classifiedBy,
    confidence: args.confidence,
    confidenceTier: args.confidenceTier,
    error: args.error,
    ...(args.timeoutOutcome ? { timeoutOutcome: args.timeoutOutcome } : {}),
  };
}

/**
 * Splits a batch's `created` cohort into wouldCapture/wouldBlock, maps `failed`/`skipped`
 * into the preview shapes, and builds the full preview result. Moved out of
 * `handleBatchCapture` (STD-2 / DEC-1504 shrink-only, smart-capture.ts is grandfathered
 * over-budget) — this module already owns the preview output contract, so the
 * cohort-splitting + mapping that feeds it belongs beside it, not in the tool handler.
 */
export function buildBatchPreviewFromResults(
  created: BatchCreatedForPreview[],
  failed: BatchFailedForPreview[],
  skipped: BatchSkippedForPreview[],
  requested: number,
  originalItems: unknown[],
  // Fix 1 (TEN-2918 follow-up): the batch's own `autoCommit` — see BatchPreviewInput's
  // `originalAutoCommit` doc comment for why `false` must survive this parameter untouched.
  originalAutoCommit?: boolean,
) {
  // WP-577 review follow-up (P2, TEN-2918): split `created` into the entries the server
  // says would actually be written vs. the ones it says would be BLOCKED (governance WHY
  // gate, glossary insert-block, etc.) — reporting a would-block item under `wouldCapture`
  // told the caller a governed capture would succeed when the real (non-preview) call
  // would be rejected at the same gate.
  const wouldCaptureEntries = created.filter((r) => !r.wouldBlock);
  const wouldBlockEntries = created.filter((r) => r.wouldBlock);
  const { failed: genuinelyFailed, unknownOutcome: timedOut } = splitBatchNotOk(failed);
  return buildBatchPreviewResult({
    // Fix 1 (TEN-2918 follow-up): report the concrete `entryId` only when it was NOT
    // auto-allocated (i.e. the caller supplied it for this item) — see WouldCaptureEntry's
    // doc comment for why an auto-ID cannot be predicted per-item within a preview batch.
    wouldCapture: wouldCaptureEntries.map((r) => ({
      ...(r.entryIdAssignedAtCapture ? { entryIdAssignedAtCapture: true as const } : { entryId: r.entryId }),
      collection: r.collection,
      name: r.name,
      ...(r.classifiedBy ? { classifiedBy: r.classifiedBy } : {}),
      ...(r.confidence != null ? { confidence: r.confidence } : {}),
      ...(r.warnings?.length ? { warnings: r.warnings } : {}),
    })),
    wouldBlock: wouldBlockEntries.map((r) => ({
      ...(r.entryIdAssignedAtCapture ? { entryIdAssignedAtCapture: true as const } : { entryId: r.entryId }),
      collection: r.collection,
      name: r.name,
      ...(r.blockingReason ? { blockingReason: r.blockingReason } : {}),
      ...(r.warnings?.length ? { warnings: r.warnings } : {}),
    })),
    failed: genuinelyFailed.map((r) => ({ index: r.entryIdx, collection: r.collection, name: r.name, error: r.error ?? "unknown error" })),
    unknownOutcome: timedOut, // WP-575 review (P1, TEN-1126) — see lib/batchTimeoutCohort.ts.
    skipped: skipped.map((s) => ({ index: s.index, name: s.name, ...(s.suggestedCollection ? { suggestedCollection: s.suggestedCollection } : {}), ...(s.confidence != null ? { confidence: s.confidence } : {}) })),
    requested,
    // WP-577 review follow-up (P2, TEN-2918): thread the caller's original batch payload
    // through so the "Capture for real" next-action can supply a non-empty `items` array —
    // omitting it left the suggestion advertising `{ action: "batch" }` alone, which fails
    // batchCaptureSchema's `.min(1)` items requirement the instant an agent follows it.
    originalItems,
    // Fix 1 (TEN-2918 follow-up): thread the batch's own autoCommit through unchanged.
    originalAutoCommit,
  });
}

export function buildBatchPreviewResult(input: BatchPreviewInput) {
  const { wouldCapture, wouldBlock = [], failed, unknownOutcome = [], skipped, requested, originalItems, originalAutoCommit } = input;
  const summary = buildBatchPreviewSummary(input);

  const lines: string[] = [`# Batch Preview — No Entries Created`, summary, ""];

  if (wouldCapture.length > 0) {
    lines.push("## Would capture");
    for (const r of wouldCapture) {
      const classNote = r.classifiedBy && r.classifiedBy !== "explicit" ? ` (${r.classifiedBy}${r.confidence != null ? ` ${r.confidence}%` : ""})` : "";
      const warnNote = r.warnings?.length ? ` — ${r.warnings.join("; ")}` : "";
      // Fix 1 (TEN-2918 follow-up): only render a concrete ID; an auto-allocated one is never
      // predictable per-item within a preview batch (see WouldCaptureEntry's doc comment).
      const idNote = r.entryId ? `**${r.entryId}**: ` : "";
      const assignedNote = r.entryIdAssignedAtCapture ? " (ID assigned at capture)" : "";
      lines.push(`- ${idNote}**${r.name}** [${r.collection}]${classNote}${warnNote}${assignedNote}`);
    }
    lines.push("");
  }

  if (wouldBlock.length > 0) {
    lines.push("## Would be BLOCKED");
    for (const b of wouldBlock) {
      const reasonNote = b.blockingReason ? ` — ${b.blockingReason}` : "";
      const idNote = b.entryId ? `**${b.entryId}**: ` : "";
      const assignedNote = b.entryIdAssignedAtCapture ? " (ID assigned at capture)" : "";
      lines.push(`- ${idNote}**${b.name}** [${b.collection}]${reasonNote}${assignedNote}`);
    }
    lines.push("");
  }

  if (skipped.length > 0) {
    lines.push("## Would be skipped — low confidence");
    for (const s of skipped) {
      const suggestion = s.suggestedCollection ? ` — best guess: \`${s.suggestedCollection}\`${s.confidence != null ? ` (${s.confidence}%)` : ""}` : " — no classification available";
      lines.push(`- **[${s.index}]** ${s.name}${suggestion}`);
    }
    lines.push("");
  }

  if (failed.length > 0) {
    lines.push("## Would fail");
    for (const f of failed) {
      lines.push(`- **[${f.index}]** ${f.name} [${f.collection}]: _${f.error}_`);
    }
    lines.push("");
  }

  // WP-575 review round (P1, TEN-1126): see lib/batchTimeoutCohort.ts — shared with the
  // real-create batch path so this section can never drift out of sync with it.
  lines.push(...renderOutcomeUnknownSection(unknownOutcome));

  lines.push("_No DB writes — call `capture action=batch` without `preview:true` to capture for real._");

  const next: NextAction[] =
    originalItems && originalItems.length > 0
      ? [{
          tool: "capture",
          description: "Capture for real",
          parameters: {
            action: "batch",
            items: originalItems,
            // Fix 1 (WP-577 review round 3, TEN-2918 follow-up): preserve the batch's own
            // autoCommit explicitly — `false` must survive (never dropped as falsy), and
            // `undefined` (never supplied) is omitted so the server default still governs.
            ...(originalAutoCommit !== undefined ? { autoCommit: originalAutoCommit } : {}),
          },
        }]
      : [];

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: {
      ...success(
        summary,
        {
          outcome: "preview" as const,
          wouldCapture,
          ...(wouldBlock.length > 0 ? { wouldBlock } : {}),
          requested,
          total: wouldCapture.length,
          failed: failed.length,
          ...(skipped.length > 0 ? { skippedLowConfidence: skipped } : {}),
          ...(failed.length > 0 ? { failedEntries: failed } : {}),
          // WP-575 review round (P1, TEN-1126): structured sibling of `failedEntries` — see
          // `UnknownOutcomeEntry`'s doc comment for why these are never merged into it.
          ...(unknownOutcome.length > 0 ? { unknownOutcomeEntries: unknownOutcome } : {}),
        },
        next,
      ),
    },
  };
}
